import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Share2, MessageCircle, CheckCircle, ArrowLeft, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { addUserShare } from '../../lib/supabase';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { trackEvent, trackException } from '../../lib/analytics';
import { toast } from 'react-toastify';

const ShareUnlock: React.FC = () => {
  const { user, userProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  // Create minimal trackable URL for WhatsApp sharing
  const getTrackableUrl = () => {
    const baseUrl = 'https://theaipanditji.com';
    // Use only utm_source for the shortest possible URL
    const utmParams = new URLSearchParams({
      utm_source: 'wa' // Shortened 'whatsapp' to 'wa'
    });
    return `${baseUrl}?${utmParams.toString()}`;
  };

  const shareMessage = `🌟 Discover your cosmic destiny with AI Pandit Ji! 🌟

Get personalized astrological readings powered by AI that reveal insights about your:
✨ Love & Relationships
💼 Career & Success  
🌟 Health & Wellness
🔮 Future Predictions

Experience the ancient wisdom of astrology with modern AI precision!

Download now: ${getTrackableUrl()}

#AIAstrology #Horoscope #PanditJi #CosmicGuidance`;

  React.useEffect(() => {
    trackEvent('share_unlock', 'page_visit', 'share_page');
  }, []);

  const handleShareAndUnlock = async () => {
    if (!user || !userProfile) return;

    setProcessing(true);
    trackEvent('share_unlock', 'attempt', 'whatsapp_share');

    try {
      // Create WhatsApp share URL with minimal trackable link
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
      
      // Track that a trackable link was shared (with minimal URL)
      trackEvent('share_tracking', 'link_shared', 'whatsapp_minimal');
      
      // Open WhatsApp in new window
      window.open(whatsappUrl, '_blank');
      
      trackEvent('share_unlock', 'whatsapp_opened', 'external_link');
      
      // Wait a moment for user to share, then automatically add the credit
      setTimeout(async () => {
        try {
          // Add a share to user's account
          const result = await addUserShare(user.phoneNumber);
          
          // Refresh user profile to get updated shares count
          await refreshUserProfile();
          
          trackEvent('share_unlock', 'success', 'credit_added');
          trackEvent('user_engagement', 'share_completed', 'whatsapp');
          
          // Track the specific share event with user ID for attribution
          trackEvent('referral_program', 'share_completed', user.phoneNumber.slice(-4)); // Last 4 digits for privacy
          
          toast.success(`🎉 Thank you for sharing! You now have ${result.shares_available} reading${result.shares_available === 1 ? '' : 's'} available.`);
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            trackEvent('navigation', 'redirect_after_share', 'dashboard');
            navigate('/');
          }, 2000);
          
        } catch (error: any) {
          console.error('Error adding share:', error);
          trackEvent('share_unlock', 'error', 'credit_add_failed');
          trackException(`Share credit addition failed: ${error.message}`, false);
          toast.error('Failed to add reading credit. Please try again.');
        } finally {
          setProcessing(false);
        }
      }, 3000); // Give user 3 seconds to share
      
    } catch (error: any) {
      console.error('Error opening WhatsApp:', error);
      setProcessing(false);
      trackEvent('share_unlock', 'error', 'whatsapp_open_failed');
      trackException(`WhatsApp share failed: ${error.message}`, false);
      toast.error('Failed to open WhatsApp. Please try again.');
    }
  };

  const handleSubscribeInstead = () => {
    trackEvent('conversion', 'subscription_from_share', 'premium_redirect');
    navigate('/premium');
  };

  if (!user || !userProfile) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-6 px-4">
      <div className="max-w-md mx-auto">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => {
            trackEvent('navigation', 'back_from_share', 'dashboard');
            navigate('/');
          }}
          className="mb-4" 
          leftIcon={<ArrowLeft size={16} />}
        >
          Back
        </Button>

        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-3"
          >
            <Share2 className="h-8 w-8 text-primary-600" />
          </motion.div>
          <h1 className="text-2xl font-bold text-primary-800 font-display">Share to Unlock</h1>
          <p className="mt-1 text-neutral-600 text-sm">
            Share on WhatsApp to unlock 1 free reading
          </p>
        </div>

        {/* Current Shares Status */}
        {userProfile?.shares_available && userProfile.shares_available > 0 && (
          <Card className="mb-4 border-green-200 bg-green-50">
            <CardContent className="py-3">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {userProfile.shares_available} Reading{userProfile.shares_available === 1 ? '' : 's'} Available
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {/* Main Share Card */}
          <Card>
            <CardContent className="py-5">
              <div className="text-center space-y-4">
                <div>
                  <h3 className="font-semibold text-lg text-neutral-900 mb-2">How it works:</h3>
                  <div className="space-y-2 text-sm text-neutral-600">
                    <p>1. Click "Share on WhatsApp" below</p>
                    <p>2. Share with friends or in a group</p>
                    <p>3. Your reading credit is automatically added!</p>
                  </div>
                </div>

                <Button
                  onClick={handleShareAndUnlock}
                  isLoading={processing}
                  leftIcon={<MessageCircle size={20} />}
                  size="lg"
                  className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white border-none py-4 text-base"
                >
                  {processing ? 'Adding Credit...' : 'Share on WhatsApp (+1 Reading)'}
                </Button>

                {processing && (
                  <p className="text-xs text-neutral-500">
                    📱 Share with your contacts - your reading credit will be added automatically!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Alternative Option */}
          <Card>
            <CardContent className="py-4">
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center">
                  <Star className="h-5 w-5 text-secondary-500 mr-2" />
                  <h4 className="font-medium text-neutral-900">Want Unlimited?</h4>
                </div>
                <p className="text-sm text-neutral-600">
                  Get unlimited readings with premium - no sharing required
                </p>
                <Button
                  onClick={handleSubscribeInstead}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  View Premium Plans
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Preview Message */}
          <Card>
            <CardContent className="py-4">
              <div>
                <h4 className="font-medium text-neutral-800 mb-2 text-sm">Share message preview:</h4>
                <div className="text-xs text-neutral-600 bg-white p-3 rounded border max-h-32 overflow-y-auto">
                  {shareMessage}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ShareUnlock;